/**
 * Coverage and assumption disclosure.
 *
 * Discovery left this product with real gaps — a third of activities carry no
 * client reference, three quarters of completed tasks have no completion time,
 * and the conversion definition is still an assumption. Surfacing that on the
 * page is what makes the rest of the numbers trustworthy rather than suspect.
 */
import { AlertTriangle, Info } from "lucide-react";

import type { Metadata, Quality } from "@/types/api";
import { ago } from "@/utils/format";

export function Disclosure({ quality, metadata }: { quality?: Quality; metadata?: Metadata }) {
  const notes = quality?.notes ?? [];
  const assumptions = metadata?.assumptions ?? [];
  const coverage: Array<[string, number | null | undefined]> = [
    ["Activities without a client link", quality?.unbridged_activity_pct],
    ["Completed tasks without a timestamp", quality?.missing_completion_pct],
    ["Employee ids unresolved", quality?.unresolved_employee_pct],
  ];
  const shown = coverage.filter(([, v]) => v != null && v > 0);

  if (!notes.length && !assumptions.length && !shown.length) return null;

  return (
    <details className="card p-3 text-[12.5px]">
      <summary className="cursor-pointer flex items-center gap-2 text-ink-muted select-none">
        <Info size={13} />
        How much to trust these numbers
        {metadata?.data_as_of && (
          <span className="ml-auto label normal-case tracking-normal">
            data as of {ago(metadata.data_as_of)}
          </span>
        )}
      </summary>

      <div className="mt-3 space-y-3">
        {shown.length > 0 && (
          <ul className="space-y-1">
            {shown.map(([label, value]) => (
              <li key={label} className="flex justify-between gap-4">
                <span className="text-ink-muted">{label}</span>
                <span className="font-mono tnum text-warn">{value!.toFixed(1)}%</span>
              </li>
            ))}
          </ul>
        )}

        {notes.map((note) => (
          <p key={note} className="text-ink-muted leading-snug">{note}</p>
        ))}

        {assumptions.length > 0 && (
          <div className="border-t border-rule pt-2 space-y-1">
            <div className="label flex items-center gap-1.5 text-warn">
              <AlertTriangle size={11} /> Assumptions in force
            </div>
            {assumptions.map((assumption) => (
              <p key={assumption} className="text-ink-muted leading-snug">{assumption}</p>
            ))}
          </div>
        )}

        {quality?.suppressed_small_samples ? (
          <p className="text-ink-faint">
            {quality.suppressed_small_samples} row(s) had ratios suppressed for a
            sample too small to rank.
          </p>
        ) : null}
      </div>
    </details>
  );
}
